import { BlotterTable, Box, useBlotterObservables, useBlotterTable } from '@talos/kyoko';
import { useMemo } from 'react';
import styled from 'styled-components';
import type { FundingRateMatrixRow, FundingRateRow } from './FundingRateBlotter.types';
import { buildFundingRateMatrix } from './fundingRateBlotterMatrix';
import { FUNDING_RATE_MATRIX_ROW_HEIGHT } from './tokens';
import { MATRIX_RATE_HEADER_CLASS, useFundingRateMatrixColumns } from './useFundingRateMatrixColumns';

interface FundingRateMatrixViewProps {
  rows: FundingRateRow[];
  /** Current toolbar update-mode, resolved to its display label (e.g. "1H"/"1D") — shown in the cell hover card. */
  periodLabel: string;
}

/** Centers the exchange header labels so they line up with the centered rate cells. */
const MatrixGrid = styled(Box)`
  .${MATRIX_RATE_HEADER_CLASS} .ag-header-cell-label {
    justify-content: center;
  }
`;

/** Asset×exchange grid — the same snapshot as the List view, pivoted with heat-colored rate cells. */
export function FundingRateMatrixView({ rows, periodLabel }: FundingRateMatrixViewProps) {
  const { exchanges, rows: matrixRows, maxAbsRate } = useMemo(() => buildFundingRateMatrix(rows), [rows]);
  const columns = useFundingRateMatrixColumns(exchanges, maxAbsRate, periodLabel);
  const { dataObservable, errorObservable } = useBlotterObservables({ data: matrixRows });

  const blotterTable = useBlotterTable<FundingRateMatrixRow>({
    dataObservable,
    errorObservable,
    columns,
    rowID: 'id' satisfies keyof FundingRateMatrixRow,
    gridOptions: { rowHeight: FUNDING_RATE_MATRIX_ROW_HEIGHT },
  });

  return (
    <MatrixGrid flex="1" w="100%" minHeight={0}>
      <BlotterTable {...blotterTable} data-testid="funding-rate-blotter-matrix" />
    </MatrixGrid>
  );
}
